import type { CctpNetwork } from "./contracts";

/** A shared object, with the version it was shared at so a transaction can name it unresolved. */
export interface SuiSharedObject {
  objectId: string;
  initialSharedVersion: number;
}

export interface SuiCctpContracts {
  messageTransmitterPackage: string;
  tokenMessengerMinterPackage: string;
  stablecoinHandlerPackage: string;
  tokenMessengerMinterState: SuiSharedObject;
  messageTransmitterState: SuiSharedObject;
  stablecoinHandlerState: SuiSharedObject;
  usdcTreasury: SuiSharedObject;
  usdcType: string;
  /** The message transmitter's `used_nonces` table, read straight for the claim check. */
  usedNoncesTable: string;
}

// From developers.circle.com/cctp/references/sui-packages; shared versions read from each network.
export const SUI_CCTP_V2: Record<CctpNetwork, SuiCctpContracts> = {
  mainnet: {
    messageTransmitterPackage: "0x16bcfcfc465f96281663a344641c017de84529370e11aa3879d0dce43ad6db87",
    tokenMessengerMinterPackage:
      "0xeb14978abfe93a37c5d5bf86a0623b923553a5f0e794daac7724f1e2fdbfb830",
    stablecoinHandlerPackage: "0x185ed207c4d64fc594882ab927f9f3c6ff957aad03df8a731ba64378faeeb2bf",
    tokenMessengerMinterState: {
      objectId: "0x06fb166941cd7bc095edc019d054a753ec3f1e4c25f28f2ecc4a6cfa0a9b1167",
      initialSharedVersion: 824760982,
    },
    messageTransmitterState: {
      objectId: "0x0c067f7d325e5b60e3179712e7783534ba1556cbb3d359d8161497e37689230c",
      initialSharedVersion: 824760979,
    },
    stablecoinHandlerState: {
      objectId: "0xa32de8a6dd0178fb05f662929d55cddb69a25c26bde4b83f89e36d17ead94c41",
      initialSharedVersion: 824760983,
    },
    usdcTreasury: {
      objectId: "0x57d6725e7a8b49a7b2a612f6bd66ab5f39fc95332ca48be421c3229d514a6de7",
      initialSharedVersion: 313333795,
    },
    usdcType: "0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7::usdc::USDC",
    usedNoncesTable: "0xbba37efd54bae90f23dd707f8189970c7c80d9fc36aa15d2d48afa61a14fc338",
  },
  testnet: {
    messageTransmitterPackage: "0xe9678cd42a81886e18e21361088071f275105636a4d3751e1d7f255211f73bbe",
    tokenMessengerMinterPackage:
      "0x267d3c0cb776eace2840f27e4d33da9c6d952f9749403f1fe6579f4962ed3c64",
    stablecoinHandlerPackage: "0xbe8479044396a45e07de2c7f14789c35b8338406eebc53b2527da56839a91561",
    tokenMessengerMinterState: {
      objectId: "0x72cb55cd14d01e6361386d6ea93eecda9fa1efc3c202b8dd69c1e4683e4c0ca0",
      initialSharedVersion: 1016313039,
    },
    messageTransmitterState: {
      objectId: "0xfee3a2b47f9ef2de2405fc63d79194307945f8ea768815cfc46083bc20fbe6ed",
      initialSharedVersion: 1016313036,
    },
    stablecoinHandlerState: {
      objectId: "0xfbd9c0517c4f0e1817445ee2be598806e3c392a465d7e3d773d1055f3f0eed32",
      initialSharedVersion: 1016313040,
    },
    usdcTreasury: {
      objectId: "0x7170137d4a6431bf83351ac025baf462909bffe2877d87716374fb42b9629ebe",
      initialSharedVersion: 68036267,
    },
    usdcType: "0xa1ec7fc00a6f40db9693ad1415d0c193ad3906494428cf252621037bd7117e29::usdc::USDC",
    usedNoncesTable: "0xd38e335fa1d09bcaffaa3ee72e31c88c19c0a223aa7717e31b51ae8d948ffa86",
  },
};

/** Sui's framework deny list, which the USDC treasury checks on every burn. */
export const SUI_DENY_LIST: Record<CctpNetwork, SuiSharedObject> = {
  mainnet: { objectId: "0x403", initialSharedVersion: 65624845 },
  testnet: { objectId: "0x403", initialSharedVersion: 22257037 },
};

/** Sui GraphQL, which unlike JSON-RPC still answers on testnet's public endpoints. */
export const SUI_GRAPHQL_URL: Record<CctpNetwork, string> = {
  mainnet: "https://graphql.mainnet.sui.io/graphql",
  testnet: "https://graphql.testnet.sui.io/graphql",
};
